import { Link } from 'react-router-dom';
import { useEventContext } from '../lib/useEvent';
import { Alert, LoadingBlock } from '../components/ui';
import {
  formatDate,
  formatMoney,
  formatRelative,
  formatSwimTimeShort,
} from '../lib/format';

export function HomePage() {
  const { event, loading, error } = useEventContext();

  if (loading) return <LoadingBlock message="Loading event details..." />;
  if (error || !event) {
    return (
      <Alert variant="error" title="Event details unavailable">
        <p>{error ?? 'Please try again shortly.'}</p>
      </Alert>
    );
  }

  const closesSoon =
    event.registrationOpen &&
    new Date(event.registrationClosesAt).getTime() - Date.now() < 14 * 86_400_000;

  return (
    <div className="stack">
      <section className="hero hero-split">
        <div className="hero-copy">
          <h1>{event.name}</h1>
          <p className="hero-tagline">{event.tagline}</p>

          <dl className="hero-facts">
            <div>
              <dt>Date</dt>
              <dd>{formatDate(event.eventDate)}</dd>
            </div>
            <div>
              <dt>First start</dt>
              <dd>
                {event.startTime} <span className="faint">{event.timezone}</span>
              </dd>
            </div>
            <div>
              <dt>Distance</dt>
              <dd>{event.distanceMeters.toLocaleString('en-GB')} m</dd>
            </div>
            <div>
              <dt>Entry fee</dt>
              <dd>{formatMoney(event.entryFeePence, event.currency)}</dd>
            </div>
            <div>
              <dt>Venue</dt>
              <dd>{event.venue}</dd>
            </div>
          </dl>

          {event.registrationOpen ? (
            <Link to="/register" className="btn btn-lg btn-secondary">
              Enter Here
            </Link>
          ) : (
            <p style={{ marginBottom: 0, fontWeight: 600 }}>Entries are closed.</p>
          )}
        </div>

        {/* Decorative marlin. A CSS mask rather than an <img> so the ink colour
            comes from the stylesheet; see .hero-art. */}
        <div className="hero-art no-print" aria-hidden="true" />
      </section>

      {event.registrationOpen && closesSoon && (
        <Alert variant="warning" title="Entries close soon">
          <p>
            Entries close {formatRelative(event.registrationClosesAt)} (
            {formatDate(event.registrationClosesAt)}).
          </p>
        </Alert>
      )}

      {!event.registrationOpen && (
        <Alert
          variant="info"
          title={
            event.placesRemaining === 0
              ? 'This event is full'
              : Date.now() < new Date(event.registrationOpensAt).getTime()
                ? 'Entries have not opened yet'
                : 'Entries have closed'
          }
        >
          <p>
            {event.placesRemaining === 0 ? (
              <>
                All places have been taken. Email{' '}
                <a href={`mailto:${event.contactEmail}`}>{event.contactEmail}</a> to ask about the
                waiting list.
              </>
            ) : Date.now() < new Date(event.registrationOpensAt).getTime() ? (
              <>
                Entries open {formatRelative(event.registrationOpensAt)} on{' '}
                {formatDate(event.registrationOpensAt)}.
              </>
            ) : (
              <>
                Entries closed on {formatDate(event.registrationClosesAt)}. Already entered? Use{' '}
                <Link to="/my-entry">Find my entry</Link> to check your details.
              </>
            )}
          </p>
        </Alert>
      )}

      <div className="grid grid-2">
        <section className="card">
          <h2>About the swim</h2>
          <p>{event.description}</p>
          <p className="muted" style={{ marginBottom: 0 }}>
            <strong>Where:</strong> {event.venueAddress}
          </p>
        </section>

        <section className="card">
          <h2>How seeding works</h2>
          <p>
            You give a predicted time when you enter. We sort every entry by that time and split the
            field into {event.seedingMode === 'waves' ? 'waves' : 'heats'}, slowest first, so you
            start alongside swimmers moving at a similar pace.
          </p>
          <p>
            Predicted times must be between {formatSwimTimeShort(event.minSeedTimeMs)} and{' '}
            {formatSwimTimeShort(event.maxSeedTimeMs)}. An honest estimate gives you the best
            experience; it is not a qualifying time.
          </p>
          <p style={{ marginBottom: 0 }}>
            {event.heatsPublished ? (
              <Link to="/start-lists">
                {event.seedingMode === 'waves' ? 'Wave' : 'Heat'} lists are published - find your
                start
              </Link>
            ) : (
              <span className="muted">
                {event.seedingMode === 'waves' ? 'Wave' : 'Heat'} lists are published closer to the
                event.
              </span>
            )}
          </p>
        </section>
      </div>

      <div className="grid grid-2">
        <section className="card">
          <h2>Entry categories</h2>
          <ul>
            {event.categories.map((category) => (
              <li key={category.id}>{category.label}</li>
            ))}
          </ul>
          <p className="faint" style={{ marginBottom: 0 }}>
            Minimum age {event.minAge} on the day of the event.
          </p>
        </section>

        <section className="card">
          <h2>Places</h2>
          <p style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--deep)', margin: 0 }}>
            {event.placesRemaining.toLocaleString('en-GB')}
          </p>
          <p className="muted">places remaining</p>
          <p className="faint" style={{ marginBottom: 0 }}>
            Entries {event.registrationOpen ? 'close' : 'closed'}{' '}
            {formatDate(event.registrationClosesAt)}.
          </p>
        </section>
      </div>
    </div>
  );
}
